import {Banner, Button} from '@wandb/components';
import {Checkbox} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {
  maybePluralize,
  maybePluralizeWord,
} from '@wandb/weave/core/util/string';
import React, {memo, useMemo, useState} from 'react';

import {useRunsHaveProtectedOutputArtifactsQuery} from '../generated/graphql';
import {Analytics} from '../services/analytics';
import {propagateErrorsContext} from '../util/errors';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface DeleteRunsButtonProps {
  selectedCount: number;
  outputArtifactsCount: number;
  activeRunsCount: number;
  projectName: string;
  entityName: string;
  onDelete(deleteArtifacts: boolean): void;
  combinedFilter: any;
}

const DeleteRunsButtonComp: React.FC<DeleteRunsButtonProps> = ({
  selectedCount,
  onDelete,
  outputArtifactsCount,
  activeRunsCount,
  projectName,
  entityName,
  combinedFilter,
}) => {
  // TODO(react-compiler): calls Analytics.track inside useMemo; compiled output
  // would call it on every render.
  'use no memo';
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [deleteArtifacts, setDeleteArtifacts] = useState(false);
  const [open, setOpen] = useState(false);
  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    setDeleteArtifacts(false);
  };

  const {
    data: runsHaveProtectedArtifactData,
    loading: runsHaveProtectedArtifactLoading,
  } = useRunsHaveProtectedOutputArtifactsQuery({
    variables: {
      projectName,
      entityName,
      filters: JSON.stringify(combinedFilter),
    },
    skip: !open,
    context: propagateErrorsContext(),
    notifyOnNetworkStatusChange: true,
  });

  const runsHaveProtectedArtifact = useMemo(() => {
    const runsHaveProtectedOutputArtifact =
      runsHaveProtectedArtifactData?.project
        ?.runsHaveProtectedOutputArtifacts ?? false;
    if (runsHaveProtectedOutputArtifact) {
      Analytics.track('source_artifact_protection', {
        location: 'DeleteRunsButton',
        entityName,
        projectName,
      });
    }
    return runsHaveProtectedOutputArtifact;
  }, [runsHaveProtectedArtifactData, entityName, projectName]);

  const isDisabled = selectedCount === 0;
  if (isDisabled) {
    return null;
  }

  const button = (
    <Button
      data-test="run-delete-popup"
      startIcon="delete"
      isActive={open}
      isDisabled={isDisabled}
      variant="secondary"
      aria-label="Delete selected runs"
      tooltip={{children: 'Delete from project'}}
      onPress={() => {
        if (selectedCount !== 0) {
          setOpen(true);
          window.analytics?.track('Delete run clicked', {
            location: analyticsLocation,
          });
        }
      }}>
      Delete
    </Button>
  );

  const showCheckbox = outputArtifactsCount > 0;

  return (
    <React.Fragment>
      {button}
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            className="w-[540px] rounded-lg"
            onInteractOutside={e => {
              e.preventDefault();
            }}>
            <Dialog.Title>
              Delete {maybePluralizeWord(selectedCount, 'run')}?
            </Dialog.Title>
            <Dialog.Description className="mt-16">
              {activeRunsCount > 0 ? (
                <span>
                  You’re deleting{' '}
                  {maybePluralize(activeRunsCount, 'active run')}, which might
                  crash live processes if your run is actually still running. We
                  suggest that you stop any runs before deleting them.
                </span>
              ) : (
                <span>
                  Delete {maybePluralize(selectedCount, 'run')}? This affects
                  all pages, including reports.{' '}
                </span>
              )}
            </Dialog.Description>
            <Dialog.Description className="mt-16 flex gap-16 flex-col">
              {showCheckbox && (
                <>
                  <div className="flex gap-8">
                    <div className="mb-auto mt-4">
                      <Checkbox
                        size="small"
                        checked={deleteArtifacts}
                        disabled={
                          runsHaveProtectedArtifact ||
                          runsHaveProtectedArtifactLoading
                        }
                        onClick={() => setDeleteArtifacts(!deleteArtifacts)}
                      />
                    </div>
                    <span
                      className={
                        runsHaveProtectedArtifact ? 'text-wb-text-tertiary' : ''
                      }>
                      Also delete the{' '}
                      {maybePluralize(outputArtifactsCount, 'artifact')} this
                      run created. Any references to{' '}
                      {outputArtifactsCount > 1 ? 'these' : 'this'}{' '}
                      {maybePluralizeWord(outputArtifactsCount, 'artifact')}{' '}
                      will be broken.
                    </span>
                  </div>
                  {deleteArtifacts && (
                    <Banner variant="error" isDismissible={false}>
                      <Banner.Body>
                        Any links from these artifacts to registries will be
                        deleted. References to linked versions will also be
                        broken.
                      </Banner.Body>
                    </Banner>
                  )}
                </>
              )}
              {runsHaveProtectedArtifact && (
                <Banner variant="error" isDismissible={false}>
                  <Banner.Body>
                    Artifact deletion cannot be enabled. You are unable to
                    delete select artifacts until all their versions have been
                    unlinked from protected registry collections.
                  </Banner.Body>
                </Banner>
              )}
            </Dialog.Description>
            <div className="mb-8 mt-24 flex items-center">
              <Button
                variant="destructive"
                isDisabled={
                  (runsHaveProtectedArtifactLoading ||
                    runsHaveProtectedArtifact) &&
                  deleteArtifacts
                }
                onClick={() => {
                  onDelete(deleteArtifacts);
                  setOpen(false);
                }}>
                {activeRunsCount > 0 ? `Yes, delete` : `Delete`}
              </Button>
              <Dialog.Close className="ml-8" asChild>
                <Button variant="ghost" ml={4}>
                  Cancel
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </React.Fragment>
  );
};

export const DeleteRunsButton = memo(DeleteRunsButtonComp);
